import React, { memo } from 'react';
import type { CSSProperties } from 'react';
import type { ProductBadge, LocalizedLabel } from '@segmently/schema-models/v2';
import { resolveLabel, type TemplateVariables } from '../../../utils/contentResolver';
import { mapFrameLayoutStyles, mapEdgeInsetsToCSS, mapCornerRadiusToCSS, DEFAULT_FONT_FALLBACK_CHAINS } from '../../../utils/styleMapper';
import { useFontOverrides } from '@modules/font-config/context/FontConfigContext';
import styles from './Badge.module.css';

function getPositionStyles(position: string | undefined): CSSProperties {
  switch (position) {
    case 'topLeft':
      return { right: 'auto', left: 'var(--badge-offset, 16px)' };
    case 'topCenter':
      return { right: 'auto', left: '50%', transform: 'translate(-50%, -50%)' };
    default:
      return {};
  }
}

export interface BadgeProps {
  badge: ProductBadge | undefined;
  locale: string;
  variables?: TemplateVariables;
  className?: string;
  'data-testid'?: string;
}

export const Badge = memo(function Badge({
  badge,
  locale,
  variables,
  className,
  'data-testid': testId,
}: BadgeProps): React.ReactElement | null {
  const fontOverrides = useFontOverrides();

  if (!badge) {
    return null;
  }

  const { appearance, layout } = badge;
  const fontChains = fontOverrides ?? DEFAULT_FONT_FALLBACK_CHAINS;

  // ProductBadge has translations (same shape as LocalizedLabel)
  const text = resolveLabel(badge as unknown as LocalizedLabel, locale, variables);

  if (!text) {
    return null;
  }

  const layoutStyles: CSSProperties = mapFrameLayoutStyles(layout);

  const badgeStyle: CSSProperties = {
    ...(appearance?.backgroundColor && { backgroundColor: appearance.backgroundColor }),
    ...(appearance?.textColor && { color: appearance.textColor }),
    ...(appearance?.fontFamily && { fontFamily: fontChains[appearance.fontFamily] || appearance.fontFamily }),
    ...(appearance?.fontWeight !== undefined && { fontWeight: appearance.fontWeight }),
    ...(appearance?.fontSize !== undefined && { fontSize: appearance.fontSize }),
    ...(appearance?.lineHeight !== undefined && { lineHeight: `${appearance.lineHeight}px` }),
    ...(appearance?.textAlign && { textAlign: appearance.textAlign as CSSProperties['textAlign'] }),
    // Border from BorderStyle object
    ...(appearance?.borders?.width !== undefined && {
      borderWidth: appearance.borders.width,
      borderStyle: 'solid',
    }),
    ...(appearance?.borders?.color && { borderColor: appearance.borders.color }),
    ...mapCornerRadiusToCSS(appearance?.borders?.radius),
    // Padding from EdgeInsets
    ...mapEdgeInsetsToCSS(appearance?.insets),
    // Position override (topLeft, topCenter, topRight)
    ...getPositionStyles(appearance?.position),
  };

  return (
    <div
      style={{ display: 'contents', ...layoutStyles }}
      data-testid={testId ? `${testId}-wrapper` : undefined}
    >
      <span
        className={className ? `${styles.badge} ${className}` : styles.badge}
        style={badgeStyle}
        data-testid={testId}
      >
        {text}
      </span>
    </div>
  );
});

Badge.displayName = 'Badge';
